<template>
  <ui-button
    class="period-picker"
    icon="fas fa-calendar-alt"
    :text="formatedDate"
    ref="picker"
    @click="onMenuClick"
    menu-content-class="ui-period-picker__menu-wrapper"
    :menu-props="{ target: 'wrapper', location: 'bottom end', offset: [0, 0] }"
  >
    <template #menu>
      <div
        class="ui-period-picker__menu"
        :class="{
          'ui-period-picker__menu-small': pickerColumns === 1,
        }"
      >
        <div class="compare-header" v-if="showCompare && showCompareOnThisPage">
          <ui-switcher
            class="compare-switch"
            :modelValue="compareEnabled"
            @change="toggleCompareReport"
            :label="t('Compare Periods')"
            :planLimits="!compareEnabled && planLimits"
          />
          <ui-button
            v-if="compareEnabled"
            @click="calcSimilarCompareRange"
            size="mini"
            :text="t('Apply Similar Past Period')"
          />
        </div>
        <div class="compare-dates" v-if="compareEnabled && showCompareOnThisPage">
          <button
            @click="setActiveRange('date1')"
            class="compare-date1"
            :class="{ active: activeCalendar === 'date1' }"
          >
            {{ textButtonDate1 }}
          </button>
          <div class="compare-vs">vs</div>
          <button
            @click="setActiveRange('date2')"
            class="compare-date2"
            :disabled="showPlanLimitsIcon"
            :class="{
              active: activeCalendar === 'date2',
              'plan-limits-warning__compare': showPlanLimitsIcon,
            }"
          >
            {{ textButtonDate2 }}
            <ui-tooltip-icon
              class="plan-limits-warning__tooltip"
              preset="error"
              v-if="showPlanLimitsIcon"
              :text="
                t(
                  'This option is no longer active due to the subscription change to the plan which doesn’t include this option or this add-on is switched off in your subscriptions settings.',
                )
              "
            ></ui-tooltip-icon>
          </button>
        </div>
        <div class="ui-period-picker__menu-content">
          <div class="ui-period-picker__menu-picker-wrapper">
            <ui-date-picker
              v-model="activeDateRangeForDatePicker"
              :columns="pickerColumns"
              @drag="handleDrag"
              ref="datePicker"
            />
          </div>

          <div class="set-times-wrp">
            <button
              v-for="item in items"
              @click="item.click(item.value)"
              class="button"
              :class="{ 'button--active': item.key === activeDateRange.date }"
              type="button"
            >
              {{ item.title }}
              <ui-tooltip-icon
                v-if="item.help"
                class="ml-4"
                preset="help"
                :text="item.help"
              ></ui-tooltip-icon>
            </button>
          </div>
        </div>

        <div class="footer">
          <div class="footer-left">
            <ui-chip class="mr-4 ml-0 hidden-sm-and-down">{{ dateFrom }}</ui-chip>
            <ui-select
              v-model="timeFrom"
              class="picker-select mr-4 ml-0"
              :items="selectHoursItemsStart"
              :hide-details="1"
              :autocomplete="false"
              @change="timeChange"
            />

            <div class="mr-4">–</div>

            <ui-chip class="mr-4 hidden-sm-and-down">{{ dateTo }}</ui-chip>
            <ui-select
              v-model="timeTo"
              class="picker-select"
              :items="selectHoursItemsEnd"
              :hide-details="1"
              :autocomplete="false"
              @change="timeChange"
            />
            <ui-select
              v-model="rangeDate1.timezone"
              @change="onTimezoneChange"
              class="timezone"
              :disabled="activeCalendar === 'date2'"
              :items="timezoneList"
              :hide-details="1"
              name="timezone"
              item-text="name"
              item-value="zone"
              :autocomplete="false"
            />
          </div>
          <div class="footer-right">
            <ui-button icon="fas fa-times" :text="t('Cancel')" @click="resetRange" />

            <ui-button
              button-style="accent"
              icon="fas fa-check"
              :text="t('Apply')"
              @click="applyRange"
            />
          </div>
        </div>
      </div>
    </template>
  </ui-button>
</template>

<script setup lang="ts">
import { usePeriodPicker } from './usePeriodPicker';

const props = withDefaults(
  defineProps<{
    showCompare?: boolean;
    format?: string;
  }>(),
  {
    showCompare: true,
    format: 'YYYY-MM-DD HH:mm:ss',
  },
);

const {
  t,
  timezoneList,
  showCompare,
  picker,
  datePicker,
  planLimits,
  showPlanLimitsIcon,
  showCompareOnThisPage,
  compareEnabled,
  activeCalendar,
  activeDateRange,
  textButtonDate1,
  textButtonDate2,
  items,
  activeDateRangeForDatePicker,
  pickerColumns,
  dateFrom,
  dateTo,
  timeFrom,
  timeTo,
  rangeDate1,
  selectHoursItemsStart,
  selectHoursItemsEnd,
  formatedDate,
  toggleCompareReport,
  calcSimilarCompareRange,
  setActiveRange,
  handleDrag,
  resetRange,
  applyRange,
  onMenuClick,
  onTimezoneChange,
  timeChange,
} = usePeriodPicker({
  showCompare: props.showCompare,
  format: props.format,
});
</script>

<style lang="scss" scoped>
.compare-dates {
  display: flex;
  position: relative;
  border-bottom: 1px solid var(--color-gray10);
}

.compare-header {
  display: flex;
  justify-content: space-between;
  padding-right: 16px;
  border-bottom: 1px solid var(--color-gray10);
  .compare-switch {
    padding: 16px 0 16px 16px;
  }
}

.compare-switch {
  :deep(.ui-label .text) {
    font-size: 18px;
  }
}

.compare-date1 {
  border-right: 1px solid var(--color-gray10);
}

.compare-date1,
.compare-date2 {
  font-size: 14px;
  text-align: center;
  width: 50%;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.compare-date1.active,
.compare-date2.active {
  color: var(--color-green);
}

.compare-vs {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 20px;
  height: 20px;
  margin: -10px 0 0 -10px;
  border-radius: 50%;
  text-align: center;
  font-size: 12px;
  font-weight: bold;
  line-height: 20px;
  background-color: var(--color-green);
  color: var(--color-white);
  z-index: 10;
}

.set-times-wrp {
  width: 100%;
  display: flex;
  flex-direction: column;
  border-left: 1px solid var(--color-gray10);
}

.button {
  text-align: left;
  padding: 6px 8px;
  border-bottom: 1px solid var(--color-gray10);
  &:last-child {
    border-bottom: none;
  }
}

.button--active {
  background-color: var(--color-green10);
  color: var(--color-green);
}

:deep(.ui-period-picker__menu-picker-wrapper) {
  padding-right: 0;
  .vc-container {
    border: none !important;
  }
}

.timezone {
  max-width: 150px;
  margin: 0 16px 0 8px;
}

.footer {
  display: flex;
  justify-content: space-between;
  padding: 8px;
  border-top: 1px solid var(--color-gray10);
}

.footer-left {
  display: flex;
  align-items: center;
}

.footer-right {
  display: flex;
}

.plan-limits-warning__tooltip {
  right: 4px;
  top: -1px;
}

.plan-limits-warning__compare {
  position: relative;
  &:after {
    display: block;
    content: '';
    position: absolute;
    border: 1px solid var(--color-red);
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
  }
}
</style>

<style lang="scss">
.period-picker {
  &__menu-date-list-wrapper {
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;

    @media only screen and (max-width: 959px) {
      .ui-menu-content-item .v-list__tile {
        padding: 0 8px;
      }
    }

    @media only screen and (max-width: 500px) {
      .ui-menu-content-item__text {
        white-space: normal;
        line-height: 1;
        font-size: 12px;
      }
    }
  }

  &__menu-date-list {
    &--no-range-picker {
      width: 200px;
    }
  }
}
</style>
